import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import Button from '../../library/button/button';
import { CarRegularIcon } from '../../shared/icons/icons';

import { GET_VOV_CMS_BUTTON } from './graphql/view-on-vehicle-button-queries';

import { useFitment, useNavigateTo, useQuery } from '../../shared/hooks/hooks';

import { clearLastPlpProduct } from '../../shared/utils/back-to-results/back-to-results';
import { getCmsComponentsInSlots } from '../../shared/utils/cms/cms';
import { getNormalizedProductType } from '../../shared/utils/product/product';
import {
  ChildrenProp,
  ProductProp
} from '../../shared/utils/prop-types/prop-types';
import { sessionStorage } from '../../shared/utils/storage/storage';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import { PRODUCT_TYPES } from '../../shared/constants/products';
import { WHEEL_CONFIGURATOR_MODAL_DATA } from '../../shared/constants/storage-keys';

import './view-on-vehicle-button.scss';

function ViewOnVehicleButton(props) {
  const {
    children,
    detailUrl,
    hideAddProduct,
    hideFilters,
    isCanonical,
    onClick: onClickProp,
    product,
    selectedSwatch,
    shouldSkipQuery
  } = props;
  const { selectedFitment } = useFitment();
  const { pathname, search } = useLocation();
  const navigateTo = useNavigateTo();

  const shouldDisplayButton = useMemo(() => {
    let viewable;
    if (!isCanonical) {
      viewable = product?.isViewableOnVehicle;
    } else {
      viewable = selectedSwatch
        ? selectedSwatch.isViewableOnVehicle
        : product?.swatches?.[0]?.isViewableOnVehicle;
    }
    return (
      Boolean(viewable) &&
      getNormalizedProductType(product) === PRODUCT_TYPES.WHEELS &&
      isArrayWithLength(product?.images) &&
      selectedFitment
    );
  }, [isCanonical, product, selectedFitment, selectedSwatch]);

  const { data, isLoading } = useQuery(GET_VOV_CMS_BUTTON, {
    skip: !shouldDisplayButton || shouldSkipQuery || children
  });
  const { contentSlots } = data?.cms?.page || {};
  let vovButtonLabel = '';

  if (contentSlots) {
    getCmsComponentsInSlots(contentSlots, cmsComponent => {
      if (cmsComponent?.name === 'Wheel-VOV-Link') {
        vovButtonLabel = cmsComponent.html;
      }
    });
  }

  const onClick = useCallback(() => {
    function storeModalData() {
      sessionStorage(WHEEL_CONFIGURATOR_MODAL_DATA, {
        assemblyId: selectedFitment?.assemblyId,
        detailUrl,
        hideAddProduct,
        hideFilters,
        isChangeStoreDisabled: true,
        product,
        shouldSkipQuery
      });
    }

    storeModalData();
    onClickProp?.();

    if (pathname.includes('fitmentresult')) {
      clearLastPlpProduct();
    }
    navigateTo(`${pathname}${search}#/wheel-visualizer`);
  }, [
    onClickProp,
    pathname,
    navigateTo,
    search,
    selectedFitment?.assemblyId,
    detailUrl,
    hideAddProduct,
    hideFilters,
    product,
    shouldSkipQuery
  ]);

  return shouldDisplayButton ? (
    <Button
      className="view-on-vehicle-button"
      onClick={onClick}
      size="regular"
      styleName="container"
    >
      {children ? (
        <>{children}</>
      ) : (
        <>
          {vovButtonLabel || isLoading ? (
            <span
              dangerouslySetInnerHTML={{ __html: vovButtonLabel }}
              styleName="vov-button-label"
            />
          ) : (
            <>
              <CarRegularIcon styleName="car-icon" />
              view on my vehicle
            </>
          )}
        </>
      )}
    </Button>
  ) : null;
}

ViewOnVehicleButton.propTypes = {
  children: ChildrenProp,
  detailUrl: PropTypes.string,
  hideAddProduct: PropTypes.bool,
  hideFilters: PropTypes.bool,
  isCanonical: PropTypes.bool,
  onClick: PropTypes.func,
  product: ProductProp,
  selectedSwatch: PropTypes.object,
  shouldSkipQuery: PropTypes.bool
};

export default ViewOnVehicleButton;
